/**
 * PraemienChart — the 12-month Prämienentwicklung card (Story UI-U7, AC 5a).
 *
 * recharts ComposedChart EXACTLY like the as-built DashboardScreen/UmsatzChart usage:
 *   Prämienvolumen → Area, stroke var(--chart-1), gradient fill 0.25→0, left axis
 *   Policenbestand → Line, stroke var(--chart-2), right axis
 * When premium is gated (all series praemie null), the premium series is DROPPED and the
 * Bestand renders as the PRIMARY line instead (no premium leak).
 *
 * Range toggle = the UI-U1 SegmentedControl (12 / 6 / 3 Monate) slicing the fixed server
 * series CLIENT-side (series.slice(-n)). Colours come ONLY from CSS vars → theme-reactive.
 *
 * [Source: apps/web/src/features/dashboard/components/UmsatzChart.tsx]
 * [Source: docs/stories/ui-u7-portfolio.md AC 5a + Dev Notes §4]
 */

import { useState } from 'react';
import {
  Area,
  ComposedChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PraemienMonatDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { SegmentedControl } from '../../../components/ui/SegmentedControl';
import { chf } from '../../../lib/format';
import { sliceSeries } from '../util';

const RANGES = [
  { value: '12', label: '12 Monate' },
  { value: '6', label: '6 Monate' },
  { value: '3', label: '3 Monate' },
];

/** 'YYYY-MM' → short German month label ('Jul'). Falls back to the raw token. */
function monthLabel(month: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString('de-CH', { month: 'short' });
}

export function PraemienChart({ series }: { series: PraemienMonatDto[] }) {
  const [range, setRange] = useState('12');

  const premiumVisible = series.some((m) => m.praemie_chf !== null);
  const n = Number(range);
  const sliced = sliceSeries(series, n).map((m) => ({
    m: monthLabel(m.month),
    v: m.praemie_chf === null ? null : Number(m.praemie_chf),
    b: m.policen,
  }));

  return (
    <Card className="rounded-xl p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Prämienentwicklung</div>
          <div className="text-xs text-muted-foreground">
            {premiumVisible
              ? 'Prämienvolumen und Policenbestand im Verlauf'
              : 'Policenbestand im Verlauf'}
          </div>
        </div>
        <SegmentedControl ariaLabel="Zeitraum" value={range} onValueChange={setRange} options={RANGES} />
      </div>
      {sliced.length === 0 ? (
        <div className="grid h-64 place-items-center text-sm text-muted-foreground">
          Noch keine Verlaufsdaten.
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={sliced} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="praemie-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="bestand-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="var(--border)" />
              <XAxis
                dataKey="m"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              />
              {premiumVisible ? (
                <>
                  <YAxis
                    yAxisId="praemie"
                    tickLine={false}
                    axisLine={false}
                    width={48}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                    tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                  />
                  <YAxis
                    yAxisId="bestand"
                    orientation="right"
                    tickLine={false}
                    axisLine={false}
                    width={32}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--popover)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--popover-foreground)',
                    }}
                    formatter={(value, name) =>
                      name === 'Prämienvolumen'
                        ? [chf(Number(value)), name]
                        : [String(value), 'Policen']
                    }
                  />
                  <Area
                    yAxisId="praemie"
                    type="monotone"
                    dataKey="v"
                    name="Prämienvolumen"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                    fill="url(#praemie-fill)"
                    connectNulls
                  />
                  <Line
                    yAxisId="bestand"
                    type="monotone"
                    dataKey="b"
                    name="Policen"
                    stroke="var(--chart-2)"
                    strokeWidth={2}
                    dot={false}
                  />
                </>
              ) : (
                <>
                  <YAxis
                    yAxisId="bestand"
                    tickLine={false}
                    axisLine={false}
                    width={40}
                    tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--popover)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--popover-foreground)',
                    }}
                    formatter={(value) => [String(value), 'Policen']}
                  />
                  <Area
                    yAxisId="bestand"
                    type="monotone"
                    dataKey="b"
                    name="Policen"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                    fill="url(#bestand-fill)"
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
